import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import MediaImage from "@/components/MediaImage";
import { stills } from "@/config/media";

// Full-bleed frame viewer. Esc closes, arrow keys navigate, focus lands on close.
export default function Lightbox({ items, index, onClose, onIndex }) {
  const closeRef = useRef(null);
  const item = stills[items[index].id];

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % items.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length, onClose, onIndex]);

  return (
    <motion.div
      className="on-dark fixed inset-0 z-[70] flex flex-col items-center justify-center bg-ink/95 p-4 backdrop-blur-md sm:p-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${item.type}, demonstration frame`}
      data-testid="lightbox"
    >
      <motion.div
        key={item.id}
        initial={{ opacity: 0, scale: 0.985 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <MediaImage
          id={item.id}
          sizes="90vw"
          eager
          className="atmos max-h-[74vh] w-auto rounded-[2px] border border-gold/30 object-contain"
        />
      </motion.div>
      <div
        className="mt-6 flex items-center gap-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => onIndex((index - 1 + items.length) % items.length)}
          aria-label="Previous frame"
          data-testid="lightbox-prev"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-primaryText transition-colors duration-300 hover:border-gold"
        >
          <ArrowLeft size={15} strokeWidth={1.4} />
        </button>
        <div className="min-w-[180px] text-center">
          <div className="display text-xl text-primaryText">{item.type}</div>
          <div className="mt-1 font-mono text-[9px] tracking-[0.25em] text-gold">
            DEMONSTRATION FRAME · {index + 1}/{items.length}
          </div>
        </div>
        <button
          onClick={() => onIndex((index + 1) % items.length)}
          aria-label="Next frame"
          data-testid="lightbox-next"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-primaryText transition-colors duration-300 hover:border-gold"
        >
          <ArrowRight size={15} strokeWidth={1.4} />
        </button>
      </div>
      <button
        ref={closeRef}
        onClick={onClose}
        aria-label="Close viewer"
        data-testid="lightbox-close"
        className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-ink/70 text-primaryText transition-colors duration-300 hover:border-gold"
      >
        <X size={16} strokeWidth={1.4} />
      </button>
    </motion.div>
  );
}
